/**
 * Emoji conversion utility — converts text emoticons and shortcodes to native Unicode emoji.
 * Applied at render time only; original text is stored as-is in the database.
 */

/** ASCII/text emoticons → Unicode emoji */
const TEXT_EMOTICONS: Record<string, string> = {
  ":'(": '\u{1F622}',  // 😢 crying face
  "'>:(": '\u{1F620}', // 😠 angry face (escaped quote variant)
  '>:(': '\u{1F620}',  // 😠 angry face
  ':D': '\u{1F604}',   // 😄 grinning face with smiling eyes
  ':P': '\u{1F61B}',   // 😛 face with tongue
  ':p': '\u{1F61B}',   // 😛 face with tongue
  ':O': '\u{1F62E}',   // 😮 face with open mouth
  ':o': '\u{1F62E}',   // 😮 face with open mouth
  ':)': '\u{1F642}',   // 🙂 slightly smiling face
  ':3': '\u{1F60A}',   // 😊 smiling face
  ':(': '\u{1F641}',   // 🙁 slightly frowning face
  ';)': '\u{1F609}',   // 😉 winking face
  ':*': '\u{1F618}',   // 😘 kissing face
  ':/': '\u{1F615}',   // 😕 confused face
  'XD': '\u{1F606}',   // 😆 laughing face
  'xD': '\u{1F606}',   // 😆 laughing face
  'B)': '\u{1F60E}',   // 😎 sunglasses face
  '<3': '\u{2764}\u{FE0F}', // ❤️ red heart
  'o.O': '\u{1F928}',  // 🤨 raised eyebrow
  'O.o': '\u{1F928}',  // 🤨 raised eyebrow
  '-_-': '\u{1F611}',  // 😑 expressionless face
  '^_^': '\u{1F60A}',  // 😊 smiling face
  'T_T': '\u{1F62D}',  // 😭 loudly crying face
};

/** Discord/Slack-style shortcodes → Unicode emoji */
const SHORTCODES: Record<string, string> = {
  // Smileys & People
  ':smile:': '\u{1F604}',
  ':grin:': '\u{1F601}',
  ':laughing:': '\u{1F606}',
  ':sweat_smile:': '\u{1F605}',
  ':rofl:': '\u{1F923}',
  ':joy:': '\u{1F602}',
  ':wink:': '\u{1F609}',
  ':blush:': '\u{1F60A}',
  ':innocent:': '\u{1F607}',
  ':heart_eyes:': '\u{1F60D}',
  ':kissing:': '\u{1F617}',
  ':kissing_heart:': '\u{1F618}',
  ':stuck_out_tongue:': '\u{1F61B}',
  ':stuck_out_tongue_winking_eye:': '\u{1F61C}',
  ':thinking:': '\u{1F914}',
  ':zipper_mouth:': '\u{1F910}',
  ':raised_eyebrow:': '\u{1F928}',
  ':neutral_face:': '\u{1F610}',
  ':expressionless:': '\u{1F611}',
  ':unamused:': '\u{1F612}',
  ':rolling_eyes:': '\u{1F644}',
  ':pensive:': '\u{1F614}',
  ':sleepy:': '\u{1F62A}',
  ':drooling:': '\u{1F924}',
  ':sleeping:': '\u{1F634}',
  ':mask:': '\u{1F637}',
  ':sunglasses:': '\u{1F60E}',
  ':nerd:': '\u{1F913}',
  ':confused:': '\u{1F615}',
  ':worried:': '\u{1F61F}',
  ':frown:': '\u{1F641}',
  ':cry:': '\u{1F622}',
  ':sob:': '\u{1F62D}',
  ':scream:': '\u{1F631}',
  ':angry:': '\u{1F620}',
  ':rage:': '\u{1F621}',
  ':skull:': '\u{1F480}',
  ':poop:': '\u{1F4A9}',
  ':clown:': '\u{1F921}',
  ':ghost:': '\u{1F47B}',
  ':alien:': '\u{1F47D}',
  ':robot:': '\u{1F916}',
  ':smiling_cat:': '\u{1F63A}',

  // Gestures & Body
  ':wave:': '\u{1F44B}',
  ':raised_hand:': '\u{270B}',
  ':ok_hand:': '\u{1F44C}',
  ':thumbsup:': '\u{1F44D}',
  ':thumbs_up:': '\u{1F44D}',
  ':+1:': '\u{1F44D}',
  ':thumbsdown:': '\u{1F44E}',
  ':thumbs_down:': '\u{1F44E}',
  ':-1:': '\u{1F44E}',
  ':fist:': '\u{270A}',
  ':punch:': '\u{1F44A}',
  ':clap:': '\u{1F44F}',
  ':raised_hands:': '\u{1F64C}',
  ':open_hands:': '\u{1F450}',
  ':handshake:': '\u{1F91D}',
  ':pray:': '\u{1F64F}',
  ':muscle:': '\u{1F4AA}',
  ':point_up:': '\u{261D}\u{FE0F}',
  ':point_down:': '\u{1F447}',
  ':point_left:': '\u{1F448}',
  ':point_right:': '\u{1F449}',
  ':middle_finger:': '\u{1F595}',
  ':v:': '\u{270C}\u{FE0F}',
  ':crossed_fingers:': '\u{1F91E}',
  ':metal:': '\u{1F918}',
  ':eyes:': '\u{1F440}',
  ':brain:': '\u{1F9E0}',

  // Hearts & Emotions
  ':heart:': '\u{2764}\u{FE0F}',
  ':red_heart:': '\u{2764}\u{FE0F}',
  ':orange_heart:': '\u{1F9E1}',
  ':yellow_heart:': '\u{1F49B}',
  ':green_heart:': '\u{1F49A}',
  ':blue_heart:': '\u{1F499}',
  ':purple_heart:': '\u{1F49C}',
  ':black_heart:': '\u{1F5A4}',
  ':white_heart:': '\u{1F90D}',
  ':broken_heart:': '\u{1F494}',
  ':heartbeat:': '\u{1F493}',
  ':sparkling_heart:': '\u{1F496}',
  ':cupid:': '\u{1F498}',
  ':gift_heart:': '\u{1F49D}',
  ':two_hearts:': '\u{1F495}',
  ':revolving_hearts:': '\u{1F49E}',
  ':kiss:': '\u{1F48B}',
  ':100:': '\u{1F4AF}',
  ':anger:': '\u{1F4A2}',
  ':boom:': '\u{1F4A5}',
  ':collision:': '\u{1F4A5}',
  ':sweat_drops:': '\u{1F4A6}',
  ':dash:': '\u{1F4A8}',
  ':hole:': '\u{1F573}\u{FE0F}',
  ':speech_balloon:': '\u{1F4AC}',
  ':thought_balloon:': '\u{1F4AD}',
  ':zzz:': '\u{1F4A4}',

  // Nature & Animals
  ':dog:': '\u{1F436}',
  ':cat:': '\u{1F431}',
  ':mouse:': '\u{1F42D}',
  ':hamster:': '\u{1F439}',
  ':rabbit:': '\u{1F430}',
  ':fox:': '\u{1F98A}',
  ':bear:': '\u{1F43B}',
  ':panda:': '\u{1F43C}',
  ':koala:': '\u{1F428}',
  ':tiger:': '\u{1F42F}',
  ':lion:': '\u{1F981}',
  ':unicorn:': '\u{1F984}',
  ':bee:': '\u{1F41D}',
  ':bug:': '\u{1F41B}',
  ':butterfly:': '\u{1F98B}',
  ':snail:': '\u{1F40C}',
  ':snake:': '\u{1F40D}',
  ':turtle:': '\u{1F422}',
  ':octopus:': '\u{1F419}',
  ':fish:': '\u{1F41F}',
  ':dolphin:': '\u{1F42C}',
  ':whale:': '\u{1F433}',

  // Food & Drink
  ':apple:': '\u{1F34E}',
  ':pizza:': '\u{1F355}',
  ':hamburger:': '\u{1F354}',
  ':fries:': '\u{1F35F}',
  ':hotdog:': '\u{1F32D}',
  ':taco:': '\u{1F32E}',
  ':burrito:': '\u{1F32F}',
  ':popcorn:': '\u{1F37F}',
  ':ice_cream:': '\u{1F368}',
  ':cake:': '\u{1F370}',
  ':cookie:': '\u{1F36A}',
  ':chocolate:': '\u{1F36B}',
  ':candy:': '\u{1F36C}',
  ':coffee:': '\u{2615}',
  ':tea:': '\u{1F375}',
  ':beer:': '\u{1F37A}',
  ':beers:': '\u{1F37B}',
  ':wine:': '\u{1F377}',
  ':cocktail:': '\u{1F378}',
  ':champagne:': '\u{1F37E}',

  // Activities & Objects
  ':soccer:': '\u{26BD}',
  ':basketball:': '\u{1F3C0}',
  ':football:': '\u{1F3C8}',
  ':baseball:': '\u{26BE}',
  ':tennis:': '\u{1F3BE}',
  ':trophy:': '\u{1F3C6}',
  ':medal:': '\u{1F3C5}',
  ':first_place:': '\u{1F947}',
  ':video_game:': '\u{1F3AE}',
  ':joystick:': '\u{1F579}\u{FE0F}',
  ':dart:': '\u{1F3AF}',
  ':guitar:': '\u{1F3B8}',
  ':microphone:': '\u{1F3A4}',
  ':headphones:': '\u{1F3A7}',
  ':art:': '\u{1F3A8}',
  ':movie:': '\u{1F3AC}',
  ':camera:': '\u{1F4F7}',
  ':tv:': '\u{1F4FA}',
  ':computer:': '\u{1F4BB}',
  ':phone:': '\u{1F4F1}',
  ':keyboard:': '\u{2328}\u{FE0F}',
  ':bulb:': '\u{1F4A1}',
  ':flashlight:': '\u{1F526}',
  ':book:': '\u{1F4D6}',
  ':books:': '\u{1F4DA}',
  ':memo:': '\u{1F4DD}',
  ':pencil:': '\u{270F}\u{FE0F}',
  ':pen:': '\u{1F58A}\u{FE0F}',
  ':paperclip:': '\u{1F4CE}',
  ':scissors:': '\u{2702}\u{FE0F}',
  ':lock:': '\u{1F512}',
  ':unlock:': '\u{1F513}',
  ':key:': '\u{1F511}',
  ':hammer:': '\u{1F528}',
  ':wrench:': '\u{1F527}',
  ':gear:': '\u{2699}\u{FE0F}',
  ':shield:': '\u{1F6E1}\u{FE0F}',
  ':link:': '\u{1F517}',
  ':package:': '\u{1F4E6}',
  ':gift:': '\u{1F381}',
  ':balloon:': '\u{1F388}',

  // Travel & Places
  ':car:': '\u{1F697}',
  ':taxi:': '\u{1F695}',
  ':bus:': '\u{1F68C}',
  ':train:': '\u{1F686}',
  ':airplane:': '\u{2708}\u{FE0F}',
  ':rocket:': '\u{1F680}',
  ':ship:': '\u{1F6A2}',
  ':house:': '\u{1F3E0}',
  ':office:': '\u{1F3E2}',
  ':hospital:': '\u{1F3E5}',
  ':earth:': '\u{1F30D}',
  ':sun:': '\u{2600}\u{FE0F}',
  ':moon:': '\u{1F319}',
  ':star:': '\u{2B50}',
  ':stars:': '\u{1F320}',
  ':cloud:': '\u{2601}\u{FE0F}',
  ':rain:': '\u{1F327}\u{FE0F}',
  ':snow:': '\u{2744}\u{FE0F}',
  ':lightning:': '\u{26A1}',
  ':rainbow:': '\u{1F308}',

  // Symbols & Flags
  ':check:': '\u{2705}',
  ':white_check_mark:': '\u{2705}',
  ':x:': '\u{274C}',
  ':warning:': '\u{26A0}\u{FE0F}',
  ':no_entry:': '\u{26D4}',
  ':prohibited:': '\u{1F6AB}',
  ':question:': '\u{2753}',
  ':exclamation:': '\u{2757}',
  ':info:': '\u{2139}\u{FE0F}',
  ':recycle:': '\u{267B}\u{FE0F}',
  ':sparkles:': '\u{2728}',
  ':fire:': '\u{1F525}',
  ':tada:': '\u{1F389}',
  ':confetti:': '\u{1F38A}',
  ':party:': '\u{1F389}',
  ':bell:': '\u{1F514}',
  ':mega:': '\u{1F4E3}',
  ':loudspeaker:': '\u{1F4E2}',
  ':flag_bg:': '\u{1F1E7}\u{1F1EC}',
  ':flag_us:': '\u{1F1FA}\u{1F1F8}',
  ':flag_eu:': '\u{1F1EA}\u{1F1FA}',
  ':pirate_flag:': '\u{1F3F4}\u{200D}\u{2620}\u{FE0F}',

  // Clock & Time
  ':hourglass:': '\u{231B}',
  ':stopwatch:': '\u{23F1}\u{FE0F}',
  ':alarm_clock:': '\u{23F0}',
  ':calendar:': '\u{1F4C5}',

  // Arrows & Indicators
  ':arrow_up:': '\u{2B06}\u{FE0F}',
  ':arrow_down:': '\u{2B07}\u{FE0F}',
  ':arrow_left:': '\u{2B05}\u{FE0F}',
  ':arrow_right:': '\u{27A1}\u{FE0F}',
  ':repeat:': '\u{1F501}',
  ':infinity:': '\u{267E}\u{FE0F}',
};

/** Regex for shortcodes like :thumbsup: */
const SHORTCODE_REGEX = /:[a-z0-9_+-]+:/gi;

/** Build boundary-safe regex for text emoticons (longest first to avoid partial matches) */
const EMOTICON_KEYS = Object.keys(TEXT_EMOTICONS)
  .sort((a, b) => b.length - a.length)
  .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

const EMOTICON_REGEX = new RegExp(
  `(?<=^|\\s)(${EMOTICON_KEYS.join('|')})(?=\\s|$)`,
  'g',
);

/**
 * Replaces text emoticons and shortcodes with native Unicode emoji.
 * Boundary-aware: won't replace emoticons embedded in URLs or words.
 */
export function replaceEmojis(text: string): string {
  if (!text) return text;

  // Pass 1: shortcodes (:thumbsup:, :fire:, etc.)
  text = text.replace(SHORTCODE_REGEX, (match) => {
    const lower = match.toLowerCase();
    return SHORTCODES[lower] ?? match;
  });

  // Pass 2: text emoticons (:D, :), <3, etc.) — only at word boundaries
  text = text.replace(EMOTICON_REGEX, (match) => TEXT_EMOTICONS[match] ?? match);

  return text;
}
